import { Instagram, Linkedin, Youtube, MessageCircle, MapPin, Phone, Mail, Globe } from "lucide-react";
import { Logo } from "./Logo";
import { CONTACT } from "./data";
import { scrollToId } from "../../lib/scroll";

const LINKS = [
  { id: "home", label: "Home" },
  { id: "story", label: "Our Story" },
  { id: "products", label: "Products" },
  { id: "b2b", label: "B2B Advantage" },
  { id: "distributor", label: "Distributor" },
];

const SOCIAL = [
  { href: CONTACT.instagram, Icon: Instagram, label: "Instagram", testId: "footer-instagram-link" },
  { href: CONTACT.youtube, Icon: Youtube, label: "YouTube", testId: "footer-youtube-link" },
  { href: CONTACT.whatsapp, Icon: MessageCircle, label: "WhatsApp", testId: "footer-whatsapp-link" },
  { href: null, Icon: Linkedin, label: "LinkedIn (coming soon)", testId: "footer-linkedin-link" },
];

const Heading = ({ children }) => <h4 className="text-xs font-bold uppercase tracking-[0.24em] text-ns-gold">{children}</h4>;

const ContactRow = ({ Icon, children, href, testId, external }) => (
  <li>
    <a href={href} data-testid={testId} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} className="flex items-start gap-3 text-[15px] text-ns-ivory/85 transition-colors duration-300 hover:text-ns-gold">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ns-gold" /> <span className="break-words">{children}</span>
    </a>
  </li>
);

export const Footer = () => (
  <footer id="contact" data-testid="site-footer" className="relative overflow-hidden bg-ns-night text-ns-ivory grain">
    <div className="mx-auto max-w-[1400px] px-5 pt-24 sm:px-8">
      <p className="font-hindi text-[2.6rem] leading-[1.35] text-ns-gold sm:text-7xl lg:text-[7rem]" data-testid="footer-display-tagline">
        स्वाद, सेहत और भरोसा भी
      </p>
      <div className="mt-20 grid gap-12 border-t border-ns-gold/20 pt-14 md:grid-cols-2 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Logo light testId="footer-logo" onClick={(e) => { e.preventDefault(); scrollToId("home"); }} />
          <p data-testid="footer-brand-description" className="mt-6 max-w-xs text-[15px] leading-relaxed text-ns-ivory/80">Independent FMCG manufacturer from West Champaran, Bihar.</p>
          <div className="mt-8 flex gap-3">
            {SOCIAL.map(({ href, Icon, label, testId }) =>
              href ? (
                <a key={testId} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={testId} className="grid h-11 w-11 place-items-center rounded-full border border-ns-gold/35 text-ns-gold transition-[background-color,color,transform] duration-300 hover:-translate-y-1 hover:bg-ns-gold hover:text-ns-night">
                  <Icon className="h-[18px] w-[18px]" />
                </a>
              ) : (
                <span key={testId} title={label} aria-label={label} data-testid={testId} className="grid h-11 w-11 cursor-not-allowed place-items-center rounded-full border border-ns-gold/20 text-ns-gold/60">
                  <Icon className="h-[18px] w-[18px]" />
                </span>
              )
            )}
          </div>
        </div>
        <div className="lg:col-span-2">
          <Heading>Quick Links</Heading>
          <ul className="mt-6 space-y-3">
            {LINKS.map((l) => (
              <li key={l.id}>
                <a href={`#${l.id}`} onClick={(e) => { e.preventDefault(); scrollToId(l.id); }} data-testid={`footer-link-${l.id}`} className="text-[15px] text-ns-ivory/85 transition-colors duration-300 hover:text-ns-gold">{l.label}</a>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-3">
          <Heading>Contact</Heading>
          <ul className="mt-6 space-y-4">
            <li data-testid="footer-location" className="flex items-start gap-3 text-[15px] text-ns-ivory/85"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ns-gold" /> {CONTACT.location}</li>
            <li data-testid="footer-sales-contact" className="text-[15px] text-ns-ivory/85"><span className="block text-xs uppercase tracking-[0.16em] text-ns-ivory/70">Orders & distribution</span>{CONTACT.salesContact}</li>
            <ContactRow Icon={Phone} href={CONTACT.phoneHref} testId="footer-phone-link">{CONTACT.phone}</ContactRow>
            <ContactRow Icon={MessageCircle} href={CONTACT.whatsapp} testId="footer-whatsapp-number-link" external>{CONTACT.phone2} (WhatsApp)</ContactRow>
            <ContactRow Icon={Mail} href={`mailto:${CONTACT.email}`} testId="footer-email-link">{CONTACT.email}</ContactRow>
            <ContactRow Icon={Globe} href={CONTACT.websiteHref} testId="footer-website-link" external>{CONTACT.website}</ContactRow>
          </ul>
        </div>
        <div className="lg:col-span-3">
          <Heading>Registrations</Heading>
          <dl className="mt-6 space-y-4 text-[15px]">
            <div data-testid="footer-fssai-lic"><dt className="text-xs uppercase tracking-[0.16em] text-ns-ivory/70">FSSAI License No.</dt><dd className="mt-1 font-medium">{CONTACT.fssai}</dd></div>
            <div data-testid="footer-gstin"><dt className="text-xs uppercase tracking-[0.16em] text-ns-ivory/70">GSTIN</dt><dd className="mt-1 font-medium">{CONTACT.gstin}</dd></div>
            <div data-testid="footer-udyam"><dt className="text-xs uppercase tracking-[0.16em] text-ns-ivory/70">Udyam Registration</dt><dd className="mt-1 break-all font-medium">{CONTACT.udyam}</dd></div>
          </dl>
        </div>
      </div>
      <div data-testid="footer-copyright" className="mt-16 flex flex-col gap-2 border-t border-ns-gold/15 py-8 pr-16 text-sm text-ns-ivory/70 sm:flex-row sm:justify-between sm:pr-20">
        <p>© 2026 NutriSwaad. All Rights Reserved.</p>
        <p className="font-hindi text-lg text-ns-gold">स्वाद, सेहत और भरोसा भी</p>
      </div>
    </div>
  </footer>
);
